import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Download, FileText, Layers, Ruler } from 'lucide-react'
import type { BuildRange, ClinSparkStudyPackage, PackageCriterion, SourceDocumentRef } from '../../types'
import {
  anchorId,
  criterionKey,
  criterionText,
  downloadText,
  known,
  pluralize,
  rows,
  scrollToElement,
  sourceShortLabel,
  toCsv,
  type Row,
} from '../../utils/parser'
import { usePersistentState } from '../../hooks/usePersistentState'
import { Badge, CopyButton, Highlight, Metric, Notice, SearchInput, Section, SegmentedControl } from './StudyUi'
import type { FocusRequest, NavigateFn } from './BuildBlueprint'

type Kind = 'inclusion' | 'exclusion'
type Item = {
  key: string
  kind: Kind
  label: string
  number: string
  text: string
  formatted: string
  population: string | null
  category: string | null
  page: unknown
  sources: SourceDocumentRef[]
  alternates: NonNullable<PackageCriterion['alternate_numbers']>
  variants: NonNullable<PackageCriterion['text_variants']>
  forms: string[]
  ranges: BuildRange[]
}

const isDensity = (input: unknown): input is 'comfortable' | 'compact' => input === 'comfortable' || input === 'compact'

function sourceKey(source: SourceDocumentRef) {
  return String(source.protocol_id || source.filename || '')
}

export default function EligibilityTab({ data, search, onSearchChange, onNavigate, focus }: {
  data: ClinSparkStudyPackage
  search: string
  onSearchChange: (next: string) => void
  onNavigate?: NavigateFn
  focus?: FocusRequest | null
}) {
  const [typeFilter, setTypeFilter] = useState<'all' | Kind>('all')
  const [population, setPopulation] = useState('all')
  const [source, setSource] = useState('all')
  const [density, setDensity] = usePersistentState<'comfortable' | 'compact'>('pc.ie.density', 'comfortable', isDensity)

  const items = useMemo<Item[]>(() => {
    const cmm = (data.study_build_design?.CMM || {}) as Row
    const formsByCriterion = new Map<string, string[]>()
    for (const mapping of rows(cmm.eligibility_reference_mapping)) {
      const prefix = String(mapping.criterion_type || '').toLowerCase().startsWith('ex') ? 'EXC' : 'INC'
      const key = criterionKey(prefix, mapping.criterion_number)
      const list = formsByCriterion.get(key) || []
      if (known(mapping.form_name) && !list.includes(String(mapping.form_name))) list.push(String(mapping.form_name))
      formsByCriterion.set(key, list)
    }
    const ranges = rows(cmm.range_dictionary || data.ranges) as unknown as BuildRange[]
    const rangesByCriterion = new Map<string, BuildRange[]>()
    for (const range of ranges) {
      const match = String(range.criterion_ref || '').match(/^(INC|EXC)\s*#\s*(.+)$/i)
      if (!match) continue
      const key = criterionKey(match[1], match[2])
      rangesByCriterion.set(key, [...(rangesByCriterion.get(key) || []), range])
    }
    return (['inclusion', 'exclusion'] as const).flatMap((kind) => rows(data.eligibility?.[kind]).map((raw, index) => {
      const criterion = raw as PackageCriterion
      const prefix = kind === 'inclusion' ? 'INC' : 'EXC'
      const number = String(criterion.criterion_number ?? raw.id ?? index + 1)
      const key = criterionKey(prefix, number)
      const text = criterionText(raw)
      return {
        key,
        kind,
        label: `${prefix} #${number}`,
        number,
        text,
        formatted: String(criterion.criterion_text_formatted || '').trim() || text,
        population: known(criterion.population) ? String(criterion.population) : null,
        category: known(criterion.category) ? String(criterion.category) : null,
        page: criterion.source_page,
        sources: (criterion.source_documents || []).filter((item): item is SourceDocumentRef => Boolean(item && typeof item === 'object')),
        alternates: criterion.alternate_numbers || [],
        variants: criterion.text_variants || [],
        forms: formsByCriterion.get(key) || [],
        ranges: rangesByCriterion.get(key) || [],
      }
    }))
  }, [data])

  const documents = useMemo(() => {
    const unique = new Map<string, SourceDocumentRef>()
    for (const item of items) for (const ref of item.sources) if (sourceKey(ref)) unique.set(sourceKey(ref), ref)
    return [...unique.entries()]
  }, [items])
  const populations = useMemo(() => [...new Set(items.map((item) => item.population).filter((p): p is string => Boolean(p)))], [items])
  const multiDocument = documents.length > 1

  const query = search.trim().toLowerCase()
  const visible = items.filter((item) =>
    (typeFilter === 'all' || item.kind === typeFilter)
    && (population === 'all' || item.population === population)
    && (source === 'all' || item.sources.some((ref) => sourceKey(ref) === source))
    && (!query || `${item.label} ${item.text} ${item.population || ''} ${item.forms.join(' ')}`.toLowerCase().includes(query)))

  // Arriving from a blueprint/range link: scroll to and flash the criterion.
  const [flash, setFlash] = useState<string | null>(null)
  useEffect(() => {
    if (!focus) return
    setTypeFilter('all')
    setPopulation('all')
    setSource('all')
    const id = window.setTimeout(() => {
      scrollToElement(document.getElementById(focus.anchor), 'center')
      setFlash(focus.anchor)
    }, 0)
    return () => window.clearTimeout(id)
  }, [focus])
  useEffect(() => {
    if (!flash) return
    const id = window.setTimeout(() => setFlash(null), 1800)
    return () => window.clearTimeout(id)
  }, [flash])

  const merged = items.filter((item) => item.sources.length > 1).length
  const variants = items.filter((item) => item.variants.length > 1).length
  const asText = (list: Item[]) => list.map((item) => `${item.label}: ${item.text}`).join('\n\n')
  const exportCsv = () => {
    const header = ['Type', 'Number', 'Population', 'Criterion', 'Source documents', 'Also numbered', 'Collected on forms', 'Ranges', 'PDF page']
    const body = items.map((item) => [
      item.kind === 'inclusion' ? 'Inclusion' : 'Exclusion',
      item.number,
      item.population || '',
      item.text,
      item.sources.map((ref) => ref.filename || ref.protocol_id).join('; '),
      item.alternates.map((alt) => `${alt.criterion_number} (${alt.filename || alt.document_role || 'source'})`).join('; '),
      item.forms.join('; '),
      item.ranges.map((range) => `${range.item_name}: ${(range.acceptable || range.stated)?.display}`).join('; '),
      item.page ?? '',
    ])
    downloadText('eligibility_criteria.csv', toCsv(header, body), 'text/csv;charset=utf-8')
  }

  const counts = { inclusion: items.filter((item) => item.kind === 'inclusion').length, exclusion: items.filter((item) => item.kind === 'exclusion').length }
  if (!items.length) {
    return (
      <Section title="Eligibility Criteria" description="Inclusion and exclusion criteria extracted from the protocol documents.">
        <Notice>No inclusion or exclusion criteria were found in the prepared protocol text. Check that the eligibility section was selected on the Documents tab.</Notice>
      </Section>
    )
  }

  return (
    <Section
      title="Eligibility Criteria"
      description={multiDocument
        ? 'Criteria from every protocol document, in protocol order. A criterion that appears in more than one document is shown once and lists each source.'
        : 'Criteria in protocol order, formatted for direct inclusion in a study build specification.'}
      actions={<>
        <CopyButton text={() => asText(visible)} label={visible.length === items.length ? 'Copy all' : `Copy ${visible.length} shown`} />
        <button type="button" className="btn-secondary" onClick={exportCsv}><Download className="mr-1.5 h-4 w-4" aria-hidden={true} />Export CSV</button>
      </>}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label="Inclusion" value={counts.inclusion} />
        <Metric label="Exclusion" value={counts.exclusion} />
        <Metric label="Shared across documents" value={multiDocument ? merged : '-'} hint="Identical criteria found in more than one document, shown once" />
        <Metric label="Wording differs" value={multiDocument ? variants : '-'} tone={variants ? 'amber' : undefined} hint="Merged criteria whose wording is not identical between documents" />
      </div>
      {variants > 0 && <Notice>{`${pluralize(variants, 'criterion', 'criteria')} ${variants === 1 ? 'is' : 'are'} worded differently between documents. Review the highlighted items before building.`}</Notice>}

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput value={search} onChange={onSearchChange} placeholder="Search criteria" className="w-full sm:w-72" />
        <SegmentedControl label="Criterion type" options={[['all', `All (${items.length})`], ['inclusion', `Inclusion (${counts.inclusion})`], ['exclusion', `Exclusion (${counts.exclusion})`]] as const} valueKey={typeFilter} onChange={setTypeFilter} />
        {populations.length > 1 && (
          <select value={population} onChange={(event) => setPopulation(event.target.value)} aria-label="Population" className="rounded-lg border border-gray-300 bg-white py-1.5 pl-2 pr-8 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
            <option value="all">All populations</option>
            {populations.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        )}
        {multiDocument && (
          <select value={source} onChange={(event) => setSource(event.target.value)} aria-label="Source document" className="rounded-lg border border-gray-300 bg-white py-1.5 pl-2 pr-8 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
            <option value="all">All documents</option>
            {documents.map(([key, ref]) => <option key={key} value={key}>{sourceShortLabel(ref)} · {ref.filename}</option>)}
          </select>
        )}
        <div className="ml-auto">
          <SegmentedControl label="Text density" options={[['comfortable', 'Full text'], ['compact', 'Compact']] as const} valueKey={density} onChange={setDensity} />
        </div>
      </div>

      {(['inclusion', 'exclusion'] as const).filter((kind) => typeFilter === 'all' || typeFilter === kind).map((kind) => {
        const list = visible.filter((item) => item.kind === kind)
        const groups: Array<{ population: string | null; items: Item[] }> = []
        for (const item of list) {
          const last = groups[groups.length - 1]
          if (last && last.population === item.population) last.items.push(item)
          else groups.push({ population: item.population, items: [item] })
        }
        return (
          <section key={kind} aria-labelledby={`ie-heading-${kind}`} className="space-y-2">
            <h4 id={`ie-heading-${kind}`} className="flex items-center gap-2 border-b border-gray-200 pb-1 font-semibold text-gray-900 dark:border-gray-700 dark:text-white">
              {kind === 'inclusion' ? 'Inclusion Criteria' : 'Exclusion Criteria'}
              <span className="text-sm font-normal text-gray-500">{list.length}</span>
            </h4>
            {list.length === 0 && <p className="py-2 text-sm text-gray-500 dark:text-gray-400">No {kind} criteria match the current filters.</p>}
            {groups.map((group, groupIndex) => (
              <div key={`${group.population}-${groupIndex}`} className="space-y-2">
                {group.population && <div className="pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{group.population}</div>}
                <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white dark:divide-gray-700/70 dark:border-gray-700 dark:bg-gray-800">
                  {group.items.map((item) => (
                    <CriterionRow key={item.key} item={item} density={density} search={search} multiDocument={multiDocument} flash={flash === anchorId('ie', item.key)} onNavigate={onNavigate} />
                  ))}
                </ol>
              </div>
            ))}
          </section>
        )
      })}
    </Section>
  )
}

function CriterionRow({ item, density, search, multiDocument, flash, onNavigate }: {
  item: Item
  density: 'comfortable' | 'compact'
  search: string
  multiDocument: boolean
  flash: boolean
  onNavigate?: NavigateFn
}) {
  const [expanded, setExpanded] = useState(false)
  const clamp = density === 'compact' && !expanded
  const differs = item.variants.length > 1
  return (
    <li id={anchorId('ie', item.key)} className={`scroll-mt-24 px-4 py-3 transition-colors ${flash ? 'bg-primary-50 dark:bg-primary-950/40' : ''}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 shrink-0 rounded-md border px-1.5 py-0.5 text-xs font-semibold tabular-nums ${item.kind === 'inclusion' ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200' : 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200'}`}>
          {item.label}
        </span>
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className={`whitespace-pre-line text-sm leading-relaxed text-gray-800 dark:text-gray-100 ${clamp ? 'line-clamp-2' : ''}`}>
            <Highlight text={clamp ? item.text : item.formatted} query={search} />
          </p>
          {density === 'compact' && item.text.length > 160 && (
            <button type="button" onClick={() => setExpanded((open) => !open)} className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400">
              {expanded ? 'Show less' : 'Show full criterion'}
            </button>
          )}
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            {item.category && <Badge>{item.category}</Badge>}
            {known(item.page) && <span className="text-gray-500 dark:text-gray-400">PDF p. {String(item.page)}</span>}
            {multiDocument && item.sources.map((ref) => (
              <Badge key={sourceKey(ref)} tone="blue" title={ref.filename}><FileText className="h-3 w-3" aria-hidden={true} />{sourceShortLabel(ref)}</Badge>
            ))}
            {item.sources.length > 1 && <Badge tone="violet" title="Found in more than one document; shown once"><Layers className="h-3 w-3" aria-hidden={true} />Shared by {item.sources.length} documents</Badge>}
            {item.alternates.map((alt) => (
              <Badge key={`${alt.criterion_number}-${alt.filename}`} title={alt.filename}>#{alt.criterion_number} in {sourceShortLabel(alt)}</Badge>
            ))}
            {differs && <Badge tone="amber"><AlertTriangle className="h-3 w-3" aria-hidden={true} />Wording differs</Badge>}
          </div>
          {(item.forms.length > 0 || item.ranges.length > 0) && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
              {item.forms.length > 0 && <span><span className="text-gray-500 dark:text-gray-400">Verified on:</span> {item.forms.join(', ')}</span>}
              {item.ranges.map((range) => {
                const label = `${range.item_name}: ${(range.acceptable || range.stated)?.display}${range.qualifier ? ` (${range.qualifier})` : ''}`
                return onNavigate ? (
                  <button key={range.range_id} type="button" onClick={() => onNavigate('ranges', anchorId('range', range.range_id))} title="Open in Ranges" className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-sky-800 hover:bg-sky-100 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
                    <Ruler className="h-3 w-3" aria-hidden={true} />{label}
                  </button>
                ) : <Badge key={range.range_id} tone="blue">{label}</Badge>
              })}
            </div>
          )}
          {differs && (
            <details className="rounded-md border border-amber-200 bg-amber-50/60 p-2 text-xs dark:border-amber-900 dark:bg-amber-950/20">
              <summary className="cursor-pointer font-medium text-amber-800 dark:text-amber-200">Compare wording in each document</summary>
              <ul className="mt-2 space-y-2">
                {item.variants.map((variant, index) => (
                  <li key={index}>
                    <div className="font-medium text-gray-700 dark:text-gray-200">{variant.source_documents.map((ref) => ref.filename || sourceShortLabel(ref)).join(', ') || 'Source'}{variant.criterion_number ? ` · #${variant.criterion_number}` : ''}</div>
                    <p className="text-gray-700 dark:text-gray-300">{variant.criterion_text}</p>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
        <CopyButton text={`${item.label}: ${item.text}`} iconOnly label={`Copy ${item.label}`} className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200" />
      </div>
    </li>
  )
}
